/**
 * DefinitionPopover — every number defends itself: the metric name opens its
 * definition (formula sentence, owner, sign-off state, lineage source view +
 * variant, full dq-flag text). Escape and click-outside dismiss (carried
 * dropdown idiom). Typed registry/lineage props only (CD-7).
 *
 * The panel renders through a portal with fixed positioning: inside the matrix
 * the trigger sits in a sticky first column, and an absolutely-positioned child
 * there is trapped under the later rows' sticky cells (sibling stacking
 * contexts) and clipped by the table's overflow container. Browser-verified
 * 12 Jul; jsdom cannot see either failure mode.
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import type { CellLineage, RegistryMetric, SignOff } from '../../types/metrics'
import TrustChip, { type TrustTone } from './TrustChip'

const SIGN_OFF_TONES: Record<SignOff, TrustTone> = {
  draft: 'warn',
  proposed: 'info',
  signed: 'good',
}

const PANEL_WIDTH = 320 // keep in sync with w-80 on the panel

function panelPosition(trigger: DOMRect): React.CSSProperties {
  const left = Math.max(8, Math.min(trigger.left, window.innerWidth - PANEL_WIDTH - 8))
  const below = trigger.bottom + 4
  // flip above the trigger when the space under it is too tight to be useful
  if (below > window.innerHeight - 240) {
    return { left, bottom: window.innerHeight - trigger.top + 4 }
  }
  return { left, top: below }
}

interface DefinitionPopoverProps {
  metric: RegistryMetric
  lineage?: CellLineage | null // from a served cell when the row has one
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="font-mono text-[9px] font-semibold uppercase tracking-wider text-mut2">{label}</div>
      <div className="mt-0.5 text-xs text-ink">{children}</div>
    </div>
  )
}

function PanelBody({ metric, lineage }: DefinitionPopoverProps) {
  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-ink">{metric.title}</span>
        <TrustChip tone={SIGN_OFF_TONES[metric.sign_off]} label={metric.sign_off} />
      </div>
      <Field label="Formula">{metric.formula}</Field>
      <Field label="Owner">{metric.owner}</Field>
      <Field label="Lineage">
        <span className="font-mono text-[11px] break-all">
          {lineage?.source_view ?? 'not served in this window'}
        </span>
        {lineage?.variant && <span className="ml-1.5 text-[11px] text-mut">variant {lineage.variant}</span>}
      </Field>
      {metric.dq_flags.length > 0 && (
        <Field label="Data quality">
          <ul className="list-disc space-y-0.5 pl-4 text-amber">
            {metric.dq_flags.map((flag) => (
              <li key={flag}>{flag}</li>
            ))}
          </ul>
        </Field>
      )}
      <Link
        to={`/metric/${metric.id}`}
        className="block pt-1 text-[11px] font-semibold text-brand hover:underline"
      >
        View metric detail →
      </Link>
    </>
  )
}

function PopoverPanel({
  metric,
  lineage,
  style,
  panelRef,
}: DefinitionPopoverProps & {
  style: React.CSSProperties
  panelRef: React.RefObject<HTMLDivElement>
}) {
  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label={`${metric.title} definition`}
      style={style}
      className="fixed z-50 w-80 space-y-2.5 rounded-xl border border-line bg-panel p-3 text-left shadow-cx"
    >
      <PanelBody metric={metric} lineage={lineage} />
    </div>,
    document.body
  )
}

/** Escape, click-outside and any scroll dismiss the panel (fixed positioning
 * detaches from its trigger the moment the page or the matrix scrolls). */
function useDismiss(
  open: boolean,
  close: () => void,
  buttonRef: React.RefObject<HTMLButtonElement>,
  panelRef: React.RefObject<HTMLDivElement>
) {
  useEffect(() => {
    if (!open) return
    const isInside = (target: Node) =>
      Boolean(buttonRef.current?.contains(target) || panelRef.current?.contains(target))
    const onMouseDown = (event: MouseEvent) => {
      if (!isInside(event.target as Node)) close()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }
    const onScroll = () => close()
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('scroll', onScroll, { capture: true, passive: true })
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('scroll', onScroll, { capture: true })
    }
  }, [open, close, buttonRef, panelRef])
}

export default function DefinitionPopover({
  metric,
  lineage,
}: DefinitionPopoverProps): React.JSX.Element {
  const [open, setOpen] = useState(false)
  const [style, setStyle] = useState<React.CSSProperties | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const close = React.useCallback(() => setOpen(false), [])

  useLayoutEffect(() => {
    if (open && buttonRef.current) {
      setStyle(panelPosition(buttonRef.current.getBoundingClientRect()))
    } else {
      setStyle(null)
    }
  }, [open])

  useDismiss(open, close, buttonRef, panelRef)

  return (
    <span className="inline-block">
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className="whitespace-nowrap text-left text-xs font-medium text-ink underline decoration-dotted decoration-mut2 underline-offset-2 transition-colors hover:text-brand"
      >
        {metric.title}
      </button>
      {open && style && <PopoverPanel metric={metric} lineage={lineage} style={style} panelRef={panelRef} />}
    </span>
  )
}
